import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { sortByName } from "@/lib/sortRows";
import { roleLabel } from "@/lib/roleLabels";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlusIcon, SparkleIcon, XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";
import TableSearch from "@/components/TableSearch";
import PagedList from "@/components/PagedList";

const LEVELS = ["beginner", "moderate", "proficient"];

const LEVEL_META = {
  beginner:   { label: "Beginner",   dot: "#71717A", cls: "text-zinc-700 border-zinc-300 bg-zinc-50" },
  moderate:   { label: "Moderate",   dot: "#F59E0B", cls: "text-amber-800 border-amber-200 bg-amber-50" },
  proficient: { label: "Proficient", dot: "#10B981", cls: "text-emerald-800 border-emerald-200 bg-emerald-50" },
};

function SkillBadge({ level }) {
  const m = LEVEL_META[level] || LEVEL_META.beginner;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm border ${m.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.dot }} />
      {m.label}
    </span>
  );
}

export default function SkillsPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [tab, setTab] = useState("matrix");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", level: "beginner" });
  const [search, setSearch] = useState("");
  const [recs, setRecs] = useState(null); // { user_id, recommendations, generated_at } | null
  const [recLoading, setRecLoading] = useState(false);
  const [gaps, setGaps] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);

  const load = async () => {
    const [u, m] = await Promise.all([api.get("/team/members"), api.get("/skills/matrix")]);
    setUsers(sortByName(u.data));
    setMatrix(m.data.skills || []);
    // currentUser comes from AuthContext and only ever has `.id`, never
    // `._id` (that mapping only happens for rows fetched through
    // lib/api.js) — this was silently defaulting to undefined.
    if (!selectedUserId && currentUser) setSelectedUserId(currentUser.id);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectedUser = useMemo(() => users.find((u) => u._id === selectedUserId), [users, selectedUserId]);
  // "owner" has identical permissions to "manager" everywhere — see
  // is_manager_or_admin() in schema_owner_role.sql.
  const canEdit = currentUser && (currentUser.id === selectedUserId || ["admin", "manager", "owner"].includes(currentUser.role));

  const addSkill = async () => {
    if (!selectedUserId) { toast.error("Select a member first"); return; }
    if (!form.name.trim()) { toast.error("Skill name required"); return; }
    try {
      await api.post(`/users/${selectedUserId}/skills`, form);
      toast.success("Skill added");
      setForm({ name: "", level: "beginner" });
      setAddOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  // Keyed by the skill's real id (user_skills.id), not its position in the
  // array — user_skills is a normal SQL table, not an embedded Mongo
  // sub-array, so a position can't reliably identify a row once anything
  // else changes underneath it.
  const updateLevel = async (skillId, level) => {
    try {
      await api.patch(`/users/${selectedUserId}/skills/${skillId}`, { level });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const generateRecommendations = async () => {
    if (!selectedUserId) return;
    setRecLoading(true);
    setRecs(null);
    try {
      const { data } = await api.post(`/skills/recommend/${selectedUserId}`);
      setRecs(data);
      if ((data.recommendations || []).length === 0) toast.info("No new recommendations right now.");
      else toast.success(`${data.recommendations.length} recommendations`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setRecLoading(false); }
  };

  const addRecommendation = async (r) => {
    try {
      await api.post(`/users/${selectedUserId}/skills`, {
        name: r.skill,
        level: ["beginner", "moderate", "proficient"].includes(r.target_level) ? r.target_level : "beginner",
      });
      toast.success(`Added ${r.skill}`);
      setRecs((prev) => prev ? { ...prev, recommendations: prev.recommendations.filter((x) => x.skill !== r.skill) } : prev);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  useEffect(() => { setRecs(null); }, [selectedUserId]);

  const generateTeamGaps = async () => {
    setGapsLoading(true);
    setGaps(null);
    try {
      const { data } = await api.post("/skills/team-gaps");
      setGaps(data);
      if ((data.gaps || []).length === 0) toast.info("No team gaps found");
      else toast.success(`${data.gaps.length} gaps identified`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setGapsLoading(false); }
  };

  const filteredMatrix = search
    ? matrix.filter((s) => s.skill.toLowerCase().includes(search.toLowerCase()))
    : matrix;


  const totals = matrix.reduce(
    (acc, s) => ({
      skills: acc.skills + 1,
      beginner: acc.beginner + s.beginner.length,
      moderate: acc.moderate + s.moderate.length,
      proficient: acc.proficient + s.proficient.length,
    }),
    { skills: 0, beginner: 0, moderate: 0, proficient: 0 }
  );

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
        <div>
          <div className="trk-uppercase-label">Capability</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Skills</h1>
        </div>
        <div className="flex items-center gap-6 text-xs font-mono">
          <div><span className="text-zinc-500">SKILLS</span> <span className="ml-1 tabular-nums text-zinc-900">{totals.skills}</span></div>
          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-500" /><span className="tabular-nums">{totals.beginner}</span></div>
          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /><span className="tabular-nums">{totals.moderate}</span></div>
          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /><span className="tabular-nums">{totals.proficient}</span></div>
        </div>
      </div>

      <div className="px-8 py-6 trk-fade-in">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm">
            <TabsTrigger value="matrix" data-testid="skills-tab-matrix">Team Matrix</TabsTrigger>
            <TabsTrigger value="member" data-testid="skills-tab-member">By Member</TabsTrigger>
            <TabsTrigger value="gaps" data-testid="skills-tab-gaps">Team Gaps</TabsTrigger>
          </TabsList>

          {/* --- MATRIX --- */}
          <TabsContent value="matrix">
            {/* Guarded on the unfiltered matrix, not the filtered one: a search
                that matches nothing must still leave the search box on screen
                to clear, so that case belongs to DataTable's own empty row. */}
            {matrix.length === 0 ? (
              <div className="border border-dashed border-border rounded-sm p-12 text-center">
                <div className="trk-uppercase-label mb-2">Empty</div>
                <h3 className="font-display text-xl font-bold mb-2">No skills tracked yet</h3>
                <p className="text-sm text-zinc-500 mb-4">Head to the "By Member" tab to add skills for each team member.</p>
                <Button onClick={() => setTab("member")} className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
                  Add skills
                </Button>
              </div>
            ) : (
              <DataTable
                toolbarStart={
                  <TableSearch
                    value={search}
                    onChange={setSearch}
                    placeholder="Search skills…"
                    label="Search skills"
                    testid="skills-matrix-search"
                    widthCls="w-64"
                  />
                }
                storageKey="skillsMatrix.visibleColumns"
                testIdPrefix="matrix"
                rows={filteredMatrix}
                rowKey={(row) => row.skill}
                pageSize={20}
                emptyMessage="No skills match this search."
                columns={[
                  {
                    key: "skill", label: "Skill", locked: true, cls: "px-4", weight: 224,
                    cell: (row) => (
                      <div className="break-words font-display font-bold tracking-tight" title={row.skill}>{row.skill}</div>
                    ),
                  },
                  ...LEVELS.map((lvl) => ({
                    key: lvl,
                    label: LEVEL_META[lvl].label,
                    head: (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: LEVEL_META[lvl].dot }} />
                        {LEVEL_META[lvl].label}
                      </span>
                    ),
                    cls: "px-4",
                    align: "align-top",
                    cell: (row) => (
                      <div className="flex flex-wrap gap-1">
                        {row[lvl].length === 0
                          ? <span className="font-mono text-xs text-zinc-300">—</span>
                          : row[lvl].map((p) => (
                              <Link
                                key={p.user_id}
                                to={`/team/${p.user_id}`}
                                className="inline-flex items-center rounded-sm border border-border bg-white px-2 py-0.5 text-xs transition-colors hover:border-zinc-400"
                              >
                                {p.name}
                              </Link>
                            ))}
                      </div>
                    ),
                  })),
                  {
                    key: "total", label: "Total", cls: "px-3 text-right", weight: 80,
                    cell: (row) => <span className="font-mono tabular-nums text-zinc-700">{row.total}</span>,
                  },
                ]}
              />
            )}
          </TabsContent>

          {/* --- BY MEMBER --- */}
          <TabsContent value="member">
            <div className="grid lg:grid-cols-[280px_1fr] gap-6">
              {/* Member list */}
              <div className="border border-border rounded-sm">
                <div className="p-3 border-b border-border trk-uppercase-label">Members ({users.length})</div>
                <div className="max-h-[70vh] overflow-y-auto">
                  {users.map((u) => (
                    <button
                      key={u._id}
                      onClick={() => setSelectedUserId(u._id)}
                      data-testid={`skills-member-${u._id}`}
                      className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-zinc-50 transition-colors ${
                        selectedUserId === u._id ? "bg-[#EFF6FF] border-l-2 border-l-[#0176D3]" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-sm break-words">{u.name}</div>
                          <div className="text-xs text-zinc-500 break-words">{roleLabel(u.role).toUpperCase()}</div>
                        </div>
                        <span className="font-mono text-xs text-zinc-400">{(u.skills || []).length}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected member's skills */}
              <div className="border border-border rounded-sm">
                {selectedUser ? (
                  <>
                    <div className="p-5 border-b border-border flex items-center justify-between">
                      <div>
                        <div className="trk-uppercase-label">{roleLabel(selectedUser.role)}</div>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight">{selectedUser.name}</h2>
                        <div className="text-xs text-zinc-500">{selectedUser.email}</div>
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            onClick={generateRecommendations}
                            disabled={recLoading}
                            data-testid="ai-recommend-button"
                            className="rounded-sm border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
                          >
                            <SparkleIcon size={14} className="mr-1" weight="fill" />
                            {recLoading ? "Thinking…" : "AI Recommendations"}
                          </Button>
                          <Dialog open={addOpen} onOpenChange={setAddOpen}>
                          <DialogTrigger asChild>
                            <Button data-testid="add-skill-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
                              <PlusIcon size={14} weight="bold" className="mr-1" /> Add Skill
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="rounded-sm max-w-md">
                            <DialogHeader><DialogTitle className="font-display">Add skill for {selectedUser.name}</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <Label className="trk-uppercase-label mb-2 block">Skill name</Label>
                                <Input
                                  value={form.name}
                                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                                  placeholder="e.g. React, Kubernetes, Figma"
                                  className="rounded-sm"
                                  data-testid="skill-name-input"
                                />
                              </div>
                              <div>
                                <Label className="trk-uppercase-label mb-2 block">Proficiency level</Label>
                                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                                  <SelectTrigger className="rounded-sm" data-testid="skill-level-select"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {LEVELS.map((l) => (
                                      <SelectItem key={l} value={l}>{LEVEL_META[l].label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <DialogFooter>
                              <Button onClick={addSkill} data-testid="save-skill-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">Add</Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        </div>
                      )}
                    </div>

                    {/* AI Recommendations */}
                    {(recLoading || recs) && (
                      <div className="border-b border-border bg-[#0A0A0A] text-white p-5" data-testid="ai-recommendations-card">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <SparkleIcon size={14} weight="fill" className="text-emerald-400" />
                            <span className="trk-uppercase-label text-white/60">AI Skill Recommendations</span>
                            <span className="text-[10px] font-mono text-white/40">· Claude Sonnet 4.5</span>
                          </div>
                          {recs && (
                            <button onClick={() => setRecs(null)} data-testid="dismiss-recs" className="text-white/60 hover:text-white">
                              <XIcon size={14} />
                            </button>
                          )}
                        </div>
                        {recLoading ? (
                          <div className="text-sm text-white/70 py-4">
                            Reading {selectedUser.name}'s open tasks and project plans…
                          </div>
                        ) : recs && recs.recommendations && recs.recommendations.length > 0 ? (
                          <div className="grid md:grid-cols-2 gap-3">
                            {recs.recommendations.map((r, i) => {
                              const level = LEVEL_META[r.target_level] || LEVEL_META.beginner;
                              return (
                                <div key={r.skill} data-testid={`rec-card-${i}`} className="border border-white/15 rounded-sm p-3 bg-white/[0.03] hover:bg-white/[0.06] transition-colors">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="font-display font-bold text-base tracking-tight">{r.skill}</div>
                                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm border ${level.cls} mt-1`}>
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: level.dot }} />
                                        Target: {level.label}
                                      </span>
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={() => addRecommendation(r)}
                                      data-testid={`add-rec-${i}`}
                                      className="rounded-sm h-7 px-2 text-xs bg-white text-[#0A0A0A] hover:bg-white/90"
                                    >
                                      <PlusIcon size={12} weight="bold" className="mr-1" /> Add
                                    </Button>
                                  </div>
                                  <p className="text-xs text-white/80 mt-2 leading-relaxed">{r.why}</p>
                                  {r.evidence && (
                                    <div className="text-[10px] font-mono text-white/40 mt-2 border-l border-white/20 pl-2 italic">
                                      Signal: {r.evidence}
                                    </div>
                                  )}
                                  {r.resources && r.resources.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                      {r.resources.map((res, ri) => (
                                        <a
                                          key={ri}
                                          href={res.url}
                                          target="_blank"
                                          rel="noreferrer"
                                          data-testid={`rec-resource-${i}-${ri}`}
                                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-sm bg-white/10 hover:bg-white/20 text-white/90 border border-white/10"
                                        >
                                          <span className="w-1 h-1 rounded-full bg-emerald-400" />
                                          {res.title}
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-sm text-white/70 py-2">No new recommendations right now — {selectedUser.name} already covers the open work.</div>
                        )}
                      </div>
                    )}

                    {/* Level breakdown */}
                    <div className="grid grid-cols-3 border-b border-border">
                      {LEVELS.map((lvl, i) => {
                        const count = (selectedUser.skills || []).filter((s) => s.level === lvl).length;
                        return (
                          <div key={lvl} className={`p-4 ${i < 2 ? "border-r border-border" : ""}`}>
                            <div className="flex items-center justify-between">
                              <div className="trk-uppercase-label">{LEVEL_META[lvl].label}</div>
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: LEVEL_META[lvl].dot }} />
                            </div>
                            <div className="font-display font-extrabold text-3xl mt-1 tabular-nums">{count}</div>
                          </div>
                        );
                      })}
                    </div>

                    {(selectedUser.skills || []).length === 0 ? (
                      <div className="p-10 text-center">
                        <div className="trk-uppercase-label mb-2">Empty</div>
                        <p className="text-sm text-zinc-500">No skills yet for this member.</p>
                      </div>
                    ) : (
                      <PagedList items={selectedUser.skills || []} pageSize={15} testIdPrefix="member-skills" className="px-5 pb-3">
                        {(rows) => (
                          <div className="-mx-5">
                        {rows.map((s) => (
                          <div key={s.id} data-testid={`skill-row-${s.id}`} className="flex items-center justify-between px-5 py-3 border-b border-border last:border-0 hover:bg-zinc-50">
                            <div className="flex items-center gap-3">
                              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: LEVEL_META[s.level]?.dot }} />
                              <span className="font-medium">{s.name}</span>
                              <SkillBadge level={s.level} />
                            </div>
                            <div className="flex items-center gap-2">
                              {canEdit && (
                                <Select value={s.level} onValueChange={(v) => updateLevel(s.id, v)}>
                                  <SelectTrigger className="rounded-sm h-7 text-xs w-[130px]" data-testid={`skill-level-${s.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {LEVELS.map((l) => <SelectItem key={l} value={l}>{LEVEL_META[l].label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>
                        ))}
                          </div>
                        )}
                      </PagedList>
                    )}
                  </>
                ) : (
                  <div className="p-10 text-sm text-zinc-500">Select a member from the list.</div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* --- TEAM GAPS --- */}
          <TabsContent value="gaps">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-zinc-500 max-w-xl">
                AI reviews the whole team's current skills against active/planning projects and open tasks —
                then flags what's under-covered and who should upskill next.
              </div>
              <Button
                onClick={generateTeamGaps}
                disabled={gapsLoading}
                data-testid="analyze-gaps-button"
                className="rounded-sm bg-[#0A0A0A] hover:bg-black text-white"
              >
                <SparkleIcon size={14} className="mr-1" weight="fill" />
                {gapsLoading ? "Analysing…" : (gaps ? "Re-analyse" : "Analyse Team Gaps")}
              </Button>
            </div>

            {gapsLoading && (
              <div className="border border-border rounded-sm p-12 text-center text-sm text-zinc-500">
                Reading the team roster, project plans, and open tasks…
              </div>
            )}

            {!gapsLoading && gaps && (gaps.gaps || []).length === 0 && (
              <div className="border border-dashed border-border rounded-sm p-12 text-center text-sm text-zinc-500">
                No critical gaps detected — the team's current skills cover the upcoming work.
              </div>
            )}

            {!gapsLoading && gaps && (gaps.gaps || []).length > 0 && (
              <div className="grid md:grid-cols-2 gap-4">
                {gaps.gaps.map((g, i) => {
                  const sev = g.severity || "medium";
                  const sevCls = sev === "high"
                    ? "text-red-700 border-red-200 bg-red-50"
                    : sev === "low"
                      ? "text-zinc-700 border-zinc-300 bg-zinc-50"
                      : "text-amber-800 border-amber-200 bg-amber-50";
                  return (
                    <div key={i} data-testid={`gap-card-${i}`} className="border border-border rounded-sm p-5 bg-white trk-hover-lift">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="min-w-0">
                          <div className="font-display font-extrabold text-lg tracking-tight">{g.skill}</div>
                          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm border ${sevCls} mt-1`}>
                            {sev} severity
                          </span>
                        </div>
                        {g.current_coverage && (
                          <div className="flex items-center gap-1.5 text-[10px] font-mono">
                            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-zinc-500" />{g.current_coverage.beginner || 0}</span>
                            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{g.current_coverage.moderate || 0}</span>
                            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{g.current_coverage.proficient || 0}</span>
                          </div>
                        )}
                      </div>

                      <p className="text-sm text-zinc-700 leading-relaxed">{g.why_needed}</p>
                      {g.evidence && (
                        <div className="text-[10px] font-mono text-zinc-500 mt-2 border-l-2 border-zinc-200 pl-2 italic">
                          Signal: {g.evidence}
                        </div>
                      )}

                      {(g.suggested_learners || []).length > 0 && (
                        <div className="mt-4">
                          <div className="trk-uppercase-label mb-2">Best learners</div>
                          <div className="space-y-2">
                            {g.suggested_learners.map((sl, si) => (
                              <div key={si} className="flex items-start justify-between gap-2 border border-border rounded-sm p-2 bg-zinc-50/60">
                                <div className="min-w-0">
                                  {sl.user_id ? (
                                    <Link to={`/team/${sl.user_id}`} className="font-medium text-sm hover:text-[#0176D3]">{sl.name}</Link>
                                  ) : (
                                    <span className="font-medium text-sm">{sl.name}</span>
                                  )}
                                  <div className="text-xs text-zinc-600">{sl.reason}</div>
                                </div>
                                {sl.role && <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{roleLabel(sl.role)}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {(g.resources || []).length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {g.resources.map((res, ri) => (
                            <a
                              key={ri}
                              href={res.url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`gap-resource-${i}-${ri}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-sm bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border border-zinc-200"
                            >
                              <span className="w-1 h-1 rounded-full bg-emerald-500" />
                              {res.title}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
